import { NewPermission } from '@core/enums';
import { SavedView } from '@core/models';
import { Entity, TableData } from '../types';
import { JupiterService } from '../@services/jupiter.service';
import { Engagement } from 'app/modules/engagements/engagements.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Dictionary } from 'tsui';
import { LoanQuote } from 'app/modules/projects/details/loans/loans.types';
import { Project, Property } from 'app/modules/projects/projects.types';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { PropertyUnit } from '@modules/projects/details/leases/tenants.types';
import { ExportScheduleForm } from '../home/@components/export-scheduling-management/export-schedule.types';

export interface JupiterStateModel {
  views: SavedView[];
  view: SavedView;
  scheduledExports: ExportScheduleForm[];
  activeExportScheduleId: number | null;
  relatedToKey: string;
  relatedToObject: CustomObject;
  isCustom: boolean;
  context: {
    relatedToKey: string;
    relatedToObject: CustomObject;
    relatedToId: number;
    service: JupiterService;
    relationValue: string | string[];
    relationFieldName: string;
    featureSupport: JupiterStateFeatureSupportModel;
    standardRelation?: {
      fieldName: string;
      asFjson?: boolean;
    };
  };
  data: TableData<any>;
  entity: Entity;
  transientEntity: Entity;
  transient: Dictionary<any>;
  slots: {
    permissions: PermissionsSlot;
    labels: LabelsSlot;
  };
  featureSupport: JupiterStateFeatureSupportModel;
  backUrl?: string;
  engagements: {
    items: Engagement[];
    contacts?: Contact[];
  };
  deals: {
    items: Project[];
  };
  properties: {
    items: Property[];
  };
  loanQuotes: {
    items: LoanQuote[];
  };
  contacts: {
    items: Contact[];
  };
  propertyUnits: {
    items: PropertyUnit[];
  };
  global: {
    isBusy: boolean;
    isSearching: boolean;
  };
}

export enum LabelSlot {
  ADD = 'ADD',
  IMPORT_VCARD = 'IMPORT_VCARD',
  EXPORT_CSV = 'EXPORT_CSV',
  BULK_ACTIONS = 'BULK_ACTIONS',
}

export enum PermissionSlot {
  MANAGE_ADD_FORM = 'MANAGE_ADD_FORM',
  MANAGE_LAYOUT = 'MANAGE_LAYOUT',
  VIEW = 'VIEW',
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  PUBLIC_CUSTOM_VIEWS = 'PUBLIC_CUSTOM_VIEWS',
  SHARING_RECORDS = 'SHARING_RECORDS',
}

export type LabelsSlot = { [key in keyof typeof LabelSlot]?: string };
export type PermissionsSlot = { [key in keyof typeof PermissionSlot]?: NewPermission };
export type JupiterStateFeatureSupportModel = {
  groupedTable: boolean;
  bulkActions?: {
    enabled: boolean;
    actions: {
      updateTags: boolean;
      deleteRows: boolean;
      createDealOrPortfolio: boolean;
    };
  };
  homeActions: {
    actions: {
      add: boolean;
      export: boolean;
      importVCard: boolean;
      createContactAssociation: boolean;
      manageView: boolean;
      viewList: boolean;
      linkDeal: boolean;
      groupBy: boolean;
    };
  };
  gridActions: {
    enabled: boolean;
    actions: {
      logCall: boolean;
      logMeeting: boolean;
      logSocialMedia: boolean;
      edit: boolean;
      delete: boolean;
      openDetails: boolean;
    };
  };
  detailsMainTabs: {
    backButton: boolean;
    avatar: boolean;
    details: boolean;
    files: boolean;
    notes: boolean;
    activities: boolean;
    engagements: boolean;
    tasks: boolean;
    linkedObjects: boolean;
    leases: boolean;
    nearby: boolean;
    deals: boolean;
    loanQuotes: boolean;
    dispositions: boolean;
    contacts: boolean;
    financials: boolean;
    contextMenu: {
      enabled: boolean;
      actions: {
        layoutManager: boolean;
        delete: boolean;
        duplicate: boolean;
        changeLayout: boolean;
        generateMergeDocument: boolean;
        merge: boolean;
        sendEmail: boolean;
        summary: boolean;
      };
    };
  };
  panels: {
    form: boolean;
    comments: boolean;
    matrix: boolean;
    table: boolean;
    images: boolean;
    'crm-table-panel': boolean;
    'crm-action-panel': boolean;
    activity: boolean;
    engagements: boolean;
    map: boolean;
    chart: boolean;
    financial_report: boolean;
    roles: boolean;
    contacts: boolean;
    unit_lease: boolean;
    unit_form: boolean;
    mailbox: boolean;
  };
  tags: {
    enabled: boolean;
  };
};

export type JupiterFeatureSupport = keyof JupiterStateFeatureSupportModel;
